import { useListener } from '#~/command/hooks.js';
import { Command } from '#~/command/index.js';
import { useToolPref } from '#~/control/hooks.js';
import { Tool } from '#~/control/index.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { Groups } from '../position/groups/index.js';
import { Presets } from '../position/presets/index.js';
import { Content } from './content.jsx';
import { useBottomTabPref } from './hooks.js';
import { Root } from './root.jsx';
import { Triggers } from './triggers.jsx';
import type { Props } from './types.js';
import { Tab } from './types.js';
import { AutoTracking } from '../auto-tracking/auto-tracking.js';

/** `<BottomTray>` component. */
export const BottomTray = (props: Props): ReactElement | null => {
  const [tab, setTab] = useBottomTabPref();
  const [tool] = useToolPref();

  const { enabled: hasAutoTracking } = useFeatureFlag(PostHogFeatureFlag.AUTOTRACKING, {
    defaultValue: false
  });

  // Command listener.
  useListener(({ command }) => {
    if (command === Command.TRAY_BOTTOM_TAB) {
      // This shortcut does not actually switch between tabs.  It just activates the presets tab.
      setTab(Tab.Presets);
    }
  });

  // Sync tabs with selected tool.
  const handleSyncTab = useLatestFn((nextTool: Tool): void => {
    if (!hasAutoTracking) {
      return;
    }

    const nextTab = nextTool === Tool.Tracking ? Tab.Tracking : Tab.Presets;

    if (nextTab !== tab) {
      setTab(nextTab);
    }
  });

  useEffect(() => {
    handleSyncTab(tool);
  }, [tool, hasAutoTracking, handleSyncTab]);

  const overflow = 'overflow-x-auto overflow-y-hidden';

  return (
    <Root {...props} tab={toAccessor([tab, setTab])}>
      <Triggers>
        {tab === Tab.Presets && <Groups className='grow overflow-x-auto overflow-y-hidden' />}
      </Triggers>

      <Content className={overflow} value={Tab.Presets}>
        {tab === Tab.Presets && <Presets />}
      </Content>

      {
        <Content className={overflow} value={Tab.Tracking}>
          {tab === Tab.Tracking && <AutoTracking />}
        </Content>
      }
    </Root>
  );
};
